// function-dispatcher.service.ts
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export interface FormRefreshData {
  refreshForm?: boolean;
  name?: string;
  action?: string;
}

@Injectable({
  providedIn: 'root'
})

export class FunctionDispatcherService {
  private registry: { [componentName: string]: any } = {};

  private refreshFormSubject = new Subject<FormRefreshData>();

  refreshForm$ = this.refreshFormSubject.asObservable();

  triggerFormRefresh(data?: FormRefreshData) {
    console.log('Triggering form refresh with data:', data);
    this.refreshFormSubject.next(data || { refreshForm: true });
  }

  registerComponent(name: string, instance: any): void {
    console.log('registerComponent:', name);
    this.registry[name] = instance;
  }

  dispatch(callString: string, context: Record<string, any> = {}): any {
    const regex = /^(\w+)\.(\w+)\((.*)\)$/;
    const match = callString.match(regex);

    if (!match) {
      console.error('Invalid function string:', callString);
      return;
    }

    const [, componentName, methodName, argsRaw] = match;

    const instance = this.registry[componentName];
    if (!instance) {
      console.error(`Component '${componentName}' not registered.`);
      return;
    }

    const method = instance[methodName];
    if (typeof method !== 'function') {
      console.error(`Method '${methodName}' not found on '${componentName}'.`);
      return;
    }

    const args = this.parseArgs(argsRaw, context);

    try {
      const result = method.apply(instance, args);

      if (result?.subscribe) {
        result.subscribe({
          next: (res: any) => console.log('Observable result:', res),
          error: (err: any) => console.error('Observable error:', err)
        });
      } else if (result instanceof Promise) {
        result.then(res => console.log('Promise result:', res)).catch(err => console.error('Promise error:', err));
      }

      return result;
    } catch (error) {
      console.error('Error during dispatch:', error);
    }
  }

  private parseArgs(argsRaw: string, context: Record<string, any>): any[] {
    const args = this.splitArguments(argsRaw);

    return args.map(arg => {
      arg = arg.trim();

      if (arg.startsWith('$')) {
        const key = arg.slice(1);
        return context[key];
      }

      if ((arg.startsWith('"') && arg.endsWith('"')) || (arg.startsWith("'") && arg.endsWith("'"))) {
        return arg.slice(1, -1);
      }

      const num = Number(arg);
      return isNaN(num) ? arg : num;
    });
  }

  private splitArguments(argString: string): string[] {
    const parts: string[] = [];
    let current = '';
    let insideQuotes = false;
    let quoteChar = '';

    for (let i = 0; i < argString.length; i++) {
      const char = argString[i];

      if ((char === "'" || char === '"') && (i === 0 || argString[i - 1] !== '\\')) {
        if (!insideQuotes) {
          insideQuotes = true;
          quoteChar = char;
          current += char;
        } else if (quoteChar === char) {
          insideQuotes = false;
          current += char;
        } else {
          current += char;
        }
      } else if (char === ',' && !insideQuotes) {
        parts.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    if (current) parts.push(current.trim());

    return parts;
  }
}
